import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Flex, Box, Text, Button, Dialog, DialogTrigger, CollapsibleSection } from '@chatsdet/ui';
import { CaretRight, CheckCircle, XCircle, ArrowUpRight } from '@phosphor-icons/react';
import type { ExploratoryBrowserSession, ReviewFinding, ReviewSeverity } from '../types';
import { useSubmitFindingFeedback, usePromoteFinding } from '../useExploratoryTests';

// ── Severity ─────────────────────────────────────────────────────────────────

const SEVERITY_ORDER: ReviewSeverity[] = ['critical', 'high', 'medium', 'low'];

const SEVERITY_META: Record<ReviewSeverity, { label: string; dot: string }> = {
  critical: { label: 'Critical', dot: 'bg-red-600' },
  high: { label: 'High', dot: 'bg-red-400' },
  medium: { label: 'Medium', dot: 'bg-amber-500' },
  low: { label: 'Low', dot: 'bg-gray-400' },
};

function formatTimestamp(ms: number | null): string | null {
  if (ms === null) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Finding row ──────────────────────────────────────────────────────────────

type FindingHandlers = {
  onConfirm: (finding: ReviewFinding) => void;
  onDismiss: (finding: ReviewFinding) => void;
  onRequestFile: (finding: ReviewFinding) => void;
  busyId: string | null;
  sessions: ExploratoryBrowserSession[];
  onSelectSession: (id: string) => void;
};

function FindingRow({ finding, handlers }: { finding: ReviewFinding; handlers: FindingHandlers }) {
  const [expanded, setExpanded] = React.useState(false);
  const ts = formatTimestamp(finding.videoTimestampMs);
  const confirmed = finding.feedbackState === 'confirmed';
  const dismissed = finding.feedbackState === 'dismissed_false_positive';
  const promoted = !!finding.promotedIssueId;
  const busy = handlers.busyId === finding.id;
  const sessionIndex =
    handlers.sessions.length > 1 && finding.browserSessionId !== null
      ? handlers.sessions.findIndex((s) => s.id === finding.browserSessionId)
      : -1;
  const session = sessionIndex >= 0 ? handlers.sessions[sessionIndex] : null;
  const sessionLabel = session ? (session.persona ?? `Session ${sessionIndex + 1}`) : null;

  return (
    <div className="border-t border-border/40 first:border-t-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/30"
      >
        <span
          aria-hidden="true"
          className={`mt-1 size-2 shrink-0 rounded-full ${SEVERITY_META[finding.severity].dot}`}
        />
        <span className="min-w-0 flex-1">
          <Text size="sm" className="break-words leading-snug">
            {finding.title}
          </Text>
          <Flex align="center" gap="xs" className="mt-0.5">
            <Text size="xs" className="text-muted-foreground">
              {SEVERITY_META[finding.severity].label}
            </Text>
            {finding.measuredOrJudgment === 'measured' && (
              <>
                <span aria-hidden="true" className="text-muted-foreground/50">
                  ·
                </span>
                <Text size="xs" className="text-muted-foreground">
                  measured
                </Text>
              </>
            )}
            {promoted && (
              <>
                <span aria-hidden="true" className="text-muted-foreground/50">
                  ·
                </span>
                <Text size="xs" className="text-emerald-600">
                  filed
                </Text>
              </>
            )}
          </Flex>
        </span>
        <CaretRight
          size={13}
          weight="bold"
          className={`mt-1 shrink-0 text-muted-foreground transition-transform duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2 px-3 pb-3">
              {finding.detail && (
                <Text size="sm" className="leading-snug">
                  {finding.detail}
                </Text>
              )}
              {finding.expectedBehavior && (
                <Text size="xs" className="text-muted-foreground">
                  <Text as="span" size="xs" weight="medium" className="text-foreground">
                    Expected:{' '}
                  </Text>
                  {finding.expectedBehavior}
                </Text>
              )}
              {finding.observedBehavior && (
                <Text size="xs" className="text-muted-foreground">
                  <Text as="span" size="xs" weight="medium" className="text-foreground">
                    Observed:{' '}
                  </Text>
                  {finding.observedBehavior}
                </Text>
              )}
              {finding.recommendation && (
                <Text size="xs" className="text-muted-foreground">
                  <Text as="span" size="xs" weight="medium" className="text-foreground">
                    Fix:{' '}
                  </Text>
                  {finding.recommendation}
                </Text>
              )}
              {(finding.primaryUrl || ts) && (
                <Flex align="center" gap="xs" className="text-muted-foreground">
                  {finding.primaryUrl && (
                    <Text size="xs" className="truncate text-muted-foreground">
                      {finding.primaryUrl}
                    </Text>
                  )}
                  {ts && session && sessionLabel ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto shrink-0 px-0 text-xs text-muted-foreground"
                      onPress={() => handlers.onSelectSession(session.id)}
                    >
                      @ {ts} · {sessionLabel}
                    </Button>
                  ) : (
                    ts && (
                      <Text size="xs" className="shrink-0 text-muted-foreground">
                        @ {ts}
                      </Text>
                    )
                  )}
                </Flex>
              )}

              {/* Actions bar — small icon buttons with tooltips */}
              <Flex align="center" gap="xs" className="mt-1 border-t border-border/40 pt-2">
                <Button
                  variant="icon"
                  size="sm"
                  iconOnly
                  tooltip="Mark as real"
                  isDisabled={busy}
                  iconLeft={
                    <CheckCircle
                      size={18}
                      weight={confirmed ? 'fill' : 'regular'}
                      className={confirmed ? 'text-emerald-600' : undefined}
                    />
                  }
                  onPress={() => handlers.onConfirm(finding)}
                />
                <Button
                  variant="icon"
                  size="sm"
                  iconOnly
                  tooltip="Dismiss as false positive"
                  isDisabled={busy}
                  iconLeft={
                    <XCircle
                      size={18}
                      weight={dismissed ? 'fill' : 'regular'}
                      className={dismissed ? 'text-muted-foreground' : undefined}
                    />
                  }
                  onPress={() => handlers.onDismiss(finding)}
                />
                <Button
                  variant="icon"
                  size="sm"
                  iconOnly
                  tooltip={promoted ? 'Already filed as an issue' : 'File as issue'}
                  isDisabled={busy || promoted}
                  iconLeft={
                    <ArrowUpRight size={18} className={promoted ? 'text-emerald-600' : undefined} />
                  }
                  onPress={() => handlers.onRequestFile(finding)}
                />
              </Flex>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── List ─────────────────────────────────────────────────────────────────────

export type ReviewFindingsListProps = {
  findings: ReviewFinding[];
  runId: string;
  /** The run's browser sessions; with more than one, a finding's timestamp names and selects its session. */
  sessions: ExploratoryBrowserSession[];
  onSelectSession: (id: string) => void;
  /** Resolve a human label for a dimension key. Falls back to Title Case. */
  dimensionLabel?: (key: string) => string | undefined;
  /**
   * Fixed group order — the flavor's dimension taxonomy. Groups render in this
   * order, with any unrecognized dimensions appended in first-appearance
   * order. Without it, group order depends on which dimension the model
   * happened to file a finding for first.
   */
  dimensionOrder?: ReadonlyArray<string>;
  /** Shown when there are no findings at all. */
  emptyLabel?: string;
};

/**
 * Severity-grouped review findings shared by every graded/ungraded review
 * flavor's sidebar (UX, spec review): grouped by dimension, sorted by
 * severity within each group, each row expandable with a confirm / dismiss /
 * file-as-issue action bar and a confirm dialog when filing.
 */
export function ReviewFindingsList({
  findings,
  runId,
  sessions,
  onSelectSession,
  dimensionLabel,
  dimensionOrder = [],
  emptyLabel = 'No findings — a clean walkthrough, or the run is still in progress.',
}: ReviewFindingsListProps) {
  const feedback = useSubmitFindingFeedback(runId);
  const promote = usePromoteFinding(runId);
  const [fileFinding, setFileFinding] = React.useState<ReviewFinding | null>(null);

  const busyId = feedback.isPending
    ? (feedback.variables?.findingId ?? null)
    : promote.isPending
      ? (promote.variables ?? null)
      : null;

  const handlers: FindingHandlers = {
    busyId,
    sessions,
    onSelectSession,
    onConfirm: (finding) => {
      feedback.mutate({ findingId: finding.id, state: 'confirmed' });
      // Confirming a finding prompts filing it as an issue (per design).
      if (!finding.promotedIssueId) setFileFinding(finding);
    },
    onDismiss: (finding) =>
      feedback.mutate({ findingId: finding.id, state: 'dismissed_false_positive' }),
    onRequestFile: (finding) => setFileFinding(finding),
  };

  // Group by type (dimension); within each group order by priority (severity).
  const severityRank = (s: ReviewSeverity) => SEVERITY_ORDER.indexOf(s);
  const extraDims = [...new Set(findings.map((f) => f.dimension))].filter(
    (k) => !dimensionOrder.includes(k)
  );
  const dimKeys = [...dimensionOrder, ...extraDims];
  const dimLabel = (k: string) => dimensionLabel?.(k) ?? k.charAt(0).toUpperCase() + k.slice(1);
  const groups = dimKeys
    .map((key) => ({
      key,
      label: dimLabel(key),
      items: findings
        .filter((f) => f.dimension === key)
        .sort((a, b) => severityRank(a.severity) - severityRank(b.severity)),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {groups.length === 0 ? (
        <Box className="p-4">
          <Text size="sm" className="text-muted-foreground">
            {emptyLabel}
          </Text>
        </Box>
      ) : (
        <div className="pb-4">
          {groups.map((g) => (
            <CollapsibleSection
              key={g.key}
              label={g.label}
              count={g.items.length}
              defaultExpanded
              triggerClassName="px-3"
              bodyClassName="pb-0"
            >
              {g.items.map((f) => (
                <FindingRow key={f.id} finding={f} handlers={handlers} />
              ))}
            </CollapsibleSection>
          ))}
        </div>
      )}

      {/* File-as-issue confirm dialog (opened by "Mark as real" or "File as issue") */}
      <DialogTrigger
        isOpen={!!fileFinding}
        onOpenChange={(open) => {
          if (!open) setFileFinding(null);
        }}
      >
        <span />
        <Dialog
          eyebrow="Promote finding"
          title="File as an issue?"
          footer={
            <>
              <Button variant="ghost" onPress={() => setFileFinding(null)}>
                Not now
              </Button>
              <Button
                variant="primary"
                isPending={promote.isPending}
                onPress={() => {
                  if (!fileFinding) return;
                  promote.mutate(fileFinding.id, { onSuccess: () => setFileFinding(null) });
                }}
              >
                File issue
              </Button>
            </>
          }
        >
          <Text size="sm" className="text-muted-foreground">
            This finding will be filed as an issue in your inbox, with its screenshot, repro
            steps, and page URL:
          </Text>
          {fileFinding && (
            <Text size="sm" weight="medium" className="mt-2">
              {fileFinding.title}
            </Text>
          )}
          {promote.isError && (
            <Text size="xs" className="mt-2 text-destructive">
              {promote.error instanceof Error ? promote.error.message : 'Failed to file issue'}
            </Text>
          )}
        </Dialog>
      </DialogTrigger>
    </>
  );
}
